import {
  useCompletionsLazyQuery,
  useCompletionsQuery
} from "@pathwright/graphql-client/src/generated"
import { flattenEdges, usePaginator } from "@pathwright/utils"
import get from "lodash/get"
import { useMemo } from "react"
import { COMPLETIONS_FILTERS } from "../utils"

const getFilter = (filter) => {
  switch (filter) {
    case COMPLETIONS_FILTERS.NEEDS_REVIEW:
      return { needs_review: true }
    case COMPLETIONS_FILTERS.REVIEWED:
      return { is_reviewed: true }
    case COMPLETIONS_FILTERS.COMPLETE:
      return { is_complete: true }
    case COMPLETIONS_FILTERS.INCOMPLETE:
      return { is_complete: false }
    case COMPLETIONS_FILTERS.NEEDS_ATTENTION:
      return { needs_attention: true }
    case COMPLETIONS_FILTERS.REVIEWABLE:
      return { reviewable: true }
    case COMPLETIONS_FILTERS.ALL:
      return {}
    default:
      return { needs_review: true }
  }
}

export const useCompletions = (options) => {
  const {
    userId,
    cohortId,
    pathId,
    sourceId,
    groupId,
    parentId,
    user_id,
    cohort_id,
    path_id,
    group_id,
    filter,
    archived_date_filter,
    ...rest
  } = options

  const first =
    typeof rest.first === "number" ? rest.first : userId || pathId ? 1042 : 50

  const requiredFilters = {
    user_id: user_id || userId,
    cohort_id: cohort_id || cohortId,
    path_id: path_id || pathId,
    group_id: group_id || groupId
  }

  // A cohort_id, user_id, path_id, or group_id filter must be provided
  const skip = !Object.values(requiredFilters).some(Boolean)

  const variables = useMemo(
    () => ({
      ...requiredFilters,
      source_id: sourceId ? parseInt(sourceId) : undefined,
      lesson_source_id: parentId ? parseInt(parentId) : parentId,
      archivedDateFilter: archived_date_filter,
      first,
      ...getFilter(filter),
      ...rest
    }),
    [options, filter]
  )

  const query = useCompletionsQuery({
    variables,
    fetchPolicy: "cache-and-network",
    skip
    // notifyOnNetworkStatusChange: true
  })

  // Oddly, at times, calling the original query's refetch results in an unclear
  // network error and the request fails to complete. Replacign the query's refetch
  // with this lazy query somehow avoids that glitch.
  const [refetch] = useCompletionsLazyQuery({
    variables,
    fetchPolicy: "network-only",
    skip
  })

  const { data, previousData, loading, error /*, refetch */ } = query
  const resolvedData = data ?? previousData
  const total = useMemo(() => get(data, "completions.total"), [resolvedData])
  const items = useMemo(() => {
    const items = resolvedData?.completions
      ? flattenEdges(resolvedData.completions)
      : []
    return items
  }, [resolvedData])

  const { loadMore, hasMore, loadingMore } = usePaginator({
    query,
    paginatorValue: resolvedData?.completions
  })

  return {
    loading,
    error,
    total,
    items,
    loadMore,
    hasMore,
    loadingMore,
    refetch,
    skipped: skip
  }
}

export default useCompletions
